import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { ConnectedClientTradeWorkflow } from 'components/common-form/sidePanel/actions/trade/backToBack/ClientTradeWorkflow';
import { ConnectedHedgingTradeWorkflow } from 'components/common-form/sidePanel/actions/trade/backToBack/HedgingTradeWorkflow';
import type { BackToBackOwnProps } from 'components/common-form/sidePanel/actions/trade/backToBack/shared';
import { TradeActionsCustomCheckbox } from 'components/common-form/sidePanel/actions/trade/TradeActionsCustomCheckbox';
import type { FormError } from 'models/formError';
import { connect } from 'react-redux';
import { getPayOffType, type PayoffType } from 'state/common-form/commonForm.selectors';
import { getBackToBackValidationErrors, getTradeConfirmation } from 'state/selectors/common/commonForm';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { tradeConfirmationSetBackToBackFieldValue } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import type {
  BackToBackType,
  TradeConfirmationBackToBack,
  TradeConfirmationBackToBackFields,
} from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';

type StateProps = {
  type: PayoffType;
  errors: FormError<TradeConfirmationBackToBackFields>[];
} & Pick<TradeConfirmationBackToBack, 'isBackToBack' | 'backToBackType'>;
type DispatchProps = { setIsBackToBack: (isBackToBack: boolean) => void };
type BackToBackWorkflowProps = StateProps & DispatchProps & BackToBackOwnProps;

type DisplayScenario = 'none' | 'hedging' | 'client';

function getDisplayScenario(backToBackType: BackToBackType | undefined): DisplayScenario {
  if (backToBackType === 'HedgingTrade') {
    return 'hedging';
  }
  if (backToBackType === 'ClientTrade') {
    return 'client';
  }
  return 'none';
}

export function BackToBackWorkflow({
  isBackToBack,
  backToBackType,
  setIsBackToBack,
  errors,
  ...props
}: BackToBackWorkflowProps) {
  if (isBackToBack.value) {
    const displayScenario = getDisplayScenario(backToBackType?.value);
    return (
      <WithInvalidFieldsContext<TradeConfirmationBackToBackFields> validationErrors={errors}>
        <TradeActionsCustomCheckbox
          isChecked={isBackToBack.value}
          id="is-back-to-back"
          trackedEvent="Enable Back to back"
          labelId="form.buttons.ConfirmBackToBack"
          onChange={setIsBackToBack}
          isReadonly={isBackToBack.isReadonly}
        >
          <div className="p-2 border border-1">
            {displayScenario === 'hedging' ? <ConnectedHedgingTradeWorkflow {...props} /> : undefined}
            {displayScenario === 'client' ? <ConnectedClientTradeWorkflow {...props} /> : undefined}
          </div>
        </TradeActionsCustomCheckbox>
      </WithInvalidFieldsContext>
    );
  }
  return undefined;
}

const mapDispatchToProps = (dispatch: ThunkDispatch, { quoteId }: BackToBackOwnProps): DispatchProps => ({
  setIsBackToBack(isBackToBack) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'isBackToBack', isBackToBack));
  },
});
const mapStateToProps = (state: AppState, { formId, workspace }: BackToBackOwnProps): StateProps => {
  const tradeConfirmation = getTradeConfirmation(state, workspace, formId);
  const errors = getBackToBackValidationErrors(state, workspace, formId);
  return {
    type: getPayOffType(state, formId, workspace),
    backToBackType: tradeConfirmation.backToBackType,
    isBackToBack: tradeConfirmation.isBackToBack,
    errors,
  };
};

export const ConnectedBackToBackWorkflow = connect(mapStateToProps, mapDispatchToProps)(BackToBackWorkflow);
